/**
 * services/permission-degraded.js — central tracking for runtime CRM
 * permission failures.
 *
 * #168. The proxy hard-gate (crm.live_chat.ALL) is already covered by
 * the boot-time PermissionErrorScreen — without that perm the inbox
 * doesn't boot at all. But other CRM permissions (Live Chat → All
 * Unassigned, Contact → Read, Channel → Read, etc.) are enforced by
 * the CRM server-side on individual forwarded calls. We can't audit
 * those upfront because session.permissions doesn't list them.
 *
 * When one of those perms is missing, services like team-directory,
 * quick-replies, and channel-meta silently 403 in the background.
 * The agent sees an inbox where channels have no colors, the team
 * picker is empty, and quick-replies don't expand — with zero hint
 * about WHY. This module turns those 403s into a single visible
 * banner pointing at the dashboard's permission checklist.
 *
 * Contract:
 *   - Background hydrates that fail with 403 should detect it via
 *     `isPermissionDeniedError(e)` and call `markDegraded(reason)`.
 *   - The PermissionDegradedBanner component subscribes to
 *     `$permissionDegraded` and renders when `degraded && !dismissed`.
 *   - Dismiss state is sessionStorage-scoped (next page load resets)
 *     so the agent doesn't get nagged forever, but the banner does
 *     come back on a fresh boot until the perms are actually granted.
 */

import { atom } from 'nanostores';
import { $session } from '../stores/session.js';

const STORAGE_KEY = 'inbox_v2_perm_degraded_dismissed';

function _initialDismissed() {
  try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}

/**
 * Reactive store. Shape:
 *   {
 *     degraded:  boolean,   // any 403 seen this session
 *     reasons:   Array<{ source, message, at }>,  // append-only audit trail
 *     dismissed: boolean,   // user clicked X (sessionStorage-backed)
 *   }
 */
export const $permissionDegraded = atom({
  degraded:  false,
  reasons:   [],
  dismissed: _initialDismissed(),
});

/**
 * Heuristic for "this error is a permission denial we should surface".
 * 403 is the strong signal; we deliberately don't match 401 because
 * that already triggers session clear + login redirect. We also avoid
 * matching CRM business-rule denials by requiring the status code,
 * not just a string match on the message.
 */
export function isPermissionDeniedError(e) {
  if (!e) return false;
  if (typeof e.status === 'number' && e.status === 403) return true;
  // Network errors with no status arrive as plain Errors with .message
  // — we don't want to false-flag those as permission issues.
  return false;
}

/**
 * Record a permission-denied event from a service hydrate. Idempotent
 * per (source, message) pair so a chatty re-fetch loop doesn't bloat
 * the reasons list. Dismissed state survives — calling markDegraded
 * does NOT un-dismiss (the user told us they understand).
 */
export function markDegraded(source, message) {
  const cur = $permissionDegraded.get();
  // Dedupe: if we already logged this exact (source, message), skip.
  if (cur.reasons.some(r => r.source === source && r.message === message)) {
    if (!cur.degraded) {
      $permissionDegraded.set({ ...cur, degraded: true });
    }
    return;
  }
  $permissionDegraded.set({
    degraded:  true,
    reasons:   [...cur.reasons, { source, message: message || '', at: Date.now() }],
    dismissed: cur.dismissed,
  });
}

/**
 * Mark a previously-degraded source as healthy. Called from each hydrate's
 * success path so the banner auto-clears once the underlying cause is
 * resolved (admin granted the missing perm, network blip recovered, etc).
 *
 * If this empties the reasons list, `degraded` flips back to false and
 * the banner disappears without the user having to refresh. We also wipe
 * the sessionStorage dismiss flag so a fresh failure later in the same
 * session can surface the banner again.
 */
export function markHealthy(source) {
  const cur = $permissionDegraded.get();
  if (cur.reasons.length === 0) return; // nothing to clear
  const remaining = cur.reasons.filter(r => r.source !== source);
  if (remaining.length === cur.reasons.length) return; // no change
  const stillDegraded = remaining.length > 0;
  if (!stillDegraded) {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
  }
  $permissionDegraded.set({
    degraded:  stillDegraded,
    reasons:   remaining,
    dismissed: stillDegraded ? cur.dismissed : false,
  });
}

export function dismissDegraded() {
  try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch {}
  const cur = $permissionDegraded.get();
  $permissionDegraded.set({ ...cur, dismissed: true });
  // Treat dismiss as an implicit "I think it's fixed, please retry" gesture.
  // The hydrates will markHealthy() on success, which clears `degraded` so
  // the banner doesn't pop right back if things really are working now.
  _scheduleRetry();
}

/**
 * Reset everything — used by clearSession() so a fresh login starts
 * clean. Wipes sessionStorage too so the next session sees the banner
 * if there are still problems.
 */
export function resetDegraded() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
  $permissionDegraded.set({ degraded: false, reasons: [], dismissed: false });
}

// Reset on logout / account switch so a fresh login starts clean. Called
// via $session subscription rather than inside clearSession() to avoid
// a circular import between session.js and this module.
$session.listen((s) => {
  if (!s) resetDegraded();
});

// ── Hydrate retry plumbing ──────────────────────────────────────────────────
// We can't import the four hydrate functions here without creating circular
// imports (each of those services already imports this file). Instead app.js
// registers a retry callback at boot via setHydrateRetryHandler(). Triggers:
//   - dismissDegraded() — explicit user gesture
//   - visibilitychange  — page came back to foreground AND degraded === true

let _retryFn = null;

/**
 * Register the function that re-fires all background hydrates. Wired in
 * app.js so this module stays free of circular imports.
 */
export function setHydrateRetryHandler(fn) {
  _retryFn = typeof fn === 'function' ? fn : null;
}

function _scheduleRetry() {
  if (!_retryFn) return;
  // Defer so the caller's state mutation finishes first.
  setTimeout(() => {
    try { _retryFn(); } catch (e) { console.warn('[perm-degraded] retry failed:', e?.message); }
  }, 0);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (!$permissionDegraded.get().degraded) return;
    _scheduleRetry();
  });
}
